
import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Users,
    Target,
    Shield,
    Zap,
    Award,
    Globe,
    ArrowRight,
    Github,
    Linkedin,
    Twitter
} from "lucide-react";
import { Link, useLocation } from "wouter";

export default function AboutPage() {
    const [, navigate] = useLocation();

    return (
        <div className="min-h-screen bg-gray-50 -mt-0">
            {/* Hero Section */}
            <div className="bg-shiftleft-900 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10"></div>
                <div className="max-w-7xl mx-auto px-6 py-24 relative z-10">
                    <div className="max-w-3xl">
                        <Badge className="bg-shiftleft-500/20 text-shiftleft-100 hover:bg-shiftleft-500/30 mb-6 border-none">
                            Our Mission
                        </Badge>
                        <h1 className="text-5xl font-bold mb-6 leading-tight">
                            Helping organizations <span className="text-shiftleft-300">Shift Left</span> for better reliability and performance.
                        </h1>
                        <p className="text-xl text-shiftleft-100 mb-8 leading-relaxed">
                            We believe that reliability and performance shouldn't be afterthoughts.
                            By shifting these critical concerns left in the development lifecycle,
                            we empower teams to build better software, faster.
                        </p>
                        <div className="flex gap-4">
                            <Button
                                size="lg"
                                className="bg-white text-shiftleft-900 hover:bg-shiftleft-50"
                                onClick={() => navigate("/login")}
                            >
                                Join Our Team
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                className="border-shiftleft-400 text-white hover:bg-shiftleft-800"
                                onClick={() => window.location.href = '#story'}
                            >
                                Read Our Story
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Abstract shape */}
                <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-shiftleft-500/30 rounded-full blur-3xl"></div>
            </div>

            {/* Stats Section */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-6 py-12">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        <div className="text-center">
                            <div className="text-4xl font-bold text-shiftleft-600 mb-2">10M+</div>
                            <div className="text-sm text-gray-500 font-medium uppercase tracking-wide">Queries Analyzed</div>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-shiftleft-600 mb-2">99.9%</div>
                            <div className="text-sm text-gray-500 font-medium uppercase tracking-wide">Uptime</div>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-shiftleft-600 mb-2">500+</div>
                            <div className="text-sm text-gray-500 font-medium uppercase tracking-wide">Enterprise Clients</div>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-shiftleft-600 mb-2">24/7</div>
                            <div className="text-sm text-gray-500 font-medium uppercase tracking-wide">Expert Support</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Our Story Section */}
            <section id="story" className="py-20 max-w-7xl mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">We bridge the gap between "Code it Fast" and "Architect it Right."</h2>
                        <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                            <div>
                                <h3 className="text-xl font-bold text-gray-800 mb-3">The Story</h3>
                                <p>
                                    In the world of data engineering, there is a dangerous divide. On one side, developers and DBAs are under immense pressure to ship features, often relying on quick fixes and isolated scripts. On the other side, Enterprise Architects design robust frameworks (TOGAF, Zachman) that rarely make it down to the code level.
                                </p>
                                <p className="mt-3">
                                    The result? Fast code that creates technical debt, and perfect architectures that never get implemented.
                                </p>
                                <p className="mt-3">
                                    We founded ShiftLeft.Digital to close this loop. We believe that "Shifting Left" isn't just about testing earlier—it's about bringing architectural intelligence directly into the developer's workflow.
                                </p>
                            </div>

                            <div className="mt-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-3">Our Philosophy: Tactical Speed, Strategic Depth</h3>
                                <p>
                                    We built our platform on a simple premise: <span className="font-semibold text-gray-900">You shouldn't have to choose between a fast tool and a smart system.</span>
                                </p>
                                <p className="mt-3">
                                    <span className="font-semibold text-shiftleft-600">ShiftLeft (The Interface):</span> We provide the tactical tools—Linters, Stress Testers, and Static Analyzers—that engineering teams need to solve immediate problems today.
                                </p>
                                <p className="mt-3">
                                    <span className="font-semibold text-shiftleft-600">Synolic™ (The Engine):</span> We back every tool with the Synolic Synthesis Engine, our proprietary architectural ontology. While you run a simple stress test, our engine is quietly mapping your dependencies, checking against architectural frameworks, and ensuring your "quick fix" aligns with your long-term strategy.
                                </p>
                            </div>

                            <div className="mt-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-3">Why "Synolic"?</h3>
                                <p>
                                    "Synolic" comes from the Greek word for <em>Synthesis</em>—the combination of ideas to form a theory or system.
                                </p>
                                <p className="mt-3">
                                    Most tools are <em>analytic</em>; they break things down to find bugs. Our engine is <em>synolic</em>; it puts things together to find meaning. It understands that a SQL Server table isn't just a storage object—it's a "Data Entity" in TOGAF, a "Container" in the C4 Model, and a "Thing" in the Zachman Framework.
                                </p>
                            </div>

                            <div className="mt-6 p-6 bg-shiftleft-50 border-l-4 border-shiftleft-600 rounded-r-lg">
                                <h3 className="text-xl font-bold text-shiftleft-900 mb-2">Our Promise</h3>
                                <p className="text-shiftleft-800">
                                    Use ShiftLeft to fix your database today. Use the power of the Synolic Engine to future-proof your enterprise for tomorrow.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-4 pt-12">
                            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 transform hover:-translate-y-1 transition-transform">
                                <Target className="w-8 h-8 text-shiftleft-600 mb-4" />
                                <h3 className="font-bold text-gray-900 mb-2">Precision</h3>
                                <p className="text-sm text-gray-500">Accurate diagnostics that pinpoint root causes instantly.</p>
                            </div>
                            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 transform hover:-translate-y-1 transition-transform">
                                <Zap className="w-8 h-8 text-yellow-500 mb-4" />
                                <h3 className="font-bold text-gray-900 mb-2">Speed</h3>
                                <p className="text-sm text-gray-500">Performance optimization that doesn't slow you down.</p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 transform hover:-translate-y-1 transition-transform">
                                <Shield className="w-8 h-8 text-green-600 mb-4" />
                                <h3 className="font-bold text-gray-900 mb-2">Security</h3>
                                <p className="text-sm text-gray-500">Enterprise-grade security built into every feature.</p>
                            </div>
                            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 transform hover:-translate-y-1 transition-transform">
                                <Users className="w-8 h-8 text-purple-600 mb-4" />
                                <h3 className="font-bold text-gray-900 mb-2">Community</h3>
                                <p className="text-sm text-gray-500">Driven by a community of passionate SQL experts.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <div className="bg-gray-900 text-white py-24">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <Badge className="bg-shiftleft-600 text-white mb-4 border-none">Our Core Values</Badge>
                        <h2 className="text-4xl font-bold">What Drives Us</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="p-8 rounded-2xl bg-shiftleft-900 border border-shiftleft-700 hover:border-shiftleft-500 transition-colors">
                            <div className="w-16 h-16 bg-shiftleft-600 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Award className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4 text-center">Tactical Speed</h3>
                            <p className="text-gray-300 text-center">
                                We deliver tools that solve today's problems immediately. No waiting, no complexity—just fast, effective solutions that engineering teams can use right now.
                            </p>
                        </div>
                        <div className="p-8 rounded-2xl bg-green-900 border border-green-700 hover:border-green-500 transition-colors">
                            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Globe className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4 text-center">Strategic Depth</h3>
                            <p className="text-gray-300 text-center">
                                Behind every quick fix is the Synolic™ engine, ensuring your solutions align with enterprise architecture frameworks and long-term strategy.
                            </p>
                        </div>
                        <div className="p-8 rounded-2xl bg-purple-900 border border-purple-700 hover:border-purple-500 transition-colors">
                            <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Users className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4 text-center">Bridge Builders</h3>
                            <p className="text-gray-300 text-center">
                                We close the gap between developers who code fast and architects who design right. Both perspectives matter, and we bring them together.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Team Section (Placeholder/Mock) */}
            <section className="py-24 max-w-7xl mx-auto px-6">
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Meet the Leadership</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                        A team of dedicated experts with decades of combined experience in SQL Server,
                        cloud architecture, and software engineering.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        {
                            name: "Alex Rivera",
                            role: "CEO & Co-Founder",
                            image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=400&h=400"
                        },
                        {
                            name: "Jishnu Nath",
                            role: "CTO & Co-Founder",
                            image: "/jishnu-nath.jpg"
                        },
                        {
                            name: "James Wilson",
                            role: "Head of Engineering",
                            image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=400&h=400"
                        }
                    ].map((member, i) => (
                        <Card key={i} className="overflow-hidden hover:shadow-xl transition-shadow border-none shadow-md">
                            <div className="aspect-square w-full overflow-hidden bg-gray-100">
                                <img
                                    src={member.image}
                                    alt={member.name}
                                    className="w-full h-full object-cover transition-transform hover:scale-105 duration-500"
                                />
                            </div>
                            <CardContent className="p-6 text-center">
                                <h3 className="text-xl font-bold text-gray-900">{member.name}</h3>
                                <p className="text-shiftleft-600 font-medium mb-4">{member.role}</p>
                                <div className="flex justify-center gap-4">
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-gray-900">
                                        <Linkedin className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-gray-900">
                                        <Twitter className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-gray-900">
                                        <Github className="w-4 h-4" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </section>

            {/* Footer CTA */}
            <div className="bg-shiftleft-50 py-24">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">Join Us on Our Journey</h2>
                    <p className="text-xl text-gray-600 mb-8">
                        We're always looking for talented individuals who are passionate about
                        technology and innovation.
                    </p>
                    <div className="flex justify-center gap-4">
                        <Button size="lg" className="bg-shiftleft-600 hover:bg-shiftleft-700 text-white">
                            View Open Positions
                        </Button>
                        <Button size="lg" variant="outline" className="bg-white hover:bg-gray-50 text-shiftleft-900">
                            Contact Us
                        </Button>
                    </div>
                </div>
            </div>

            {/* Simple Footer (reuse main footer in App layout if consistent, but here for standalone look if needed, 
                or just let main layout handle it. Since App structure seemed to have footer in Landing but not global...
                I will add a simple copyright here to match landing style if not wrapped) 
            */}
            <footer className="bg-gray-900 text-gray-400 py-12 border-t border-gray-800">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <p>&copy; {new Date().getFullYear()} CloudBaud, LLC. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
}
